/**
 * useHistory Hook
 * Provides undo/redo functionality for state management
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { HISTORY_CONFIG } from '@/constants';
import type { NetworkState } from '@/types';
import { log, LOG_CATEGORIES } from '@/utils/logger';

// ============================================================
// TYPES
// ============================================================

/** A single history entry containing before and after states */
export interface HistoryEntry<T = NetworkState> {
  /** State before the change */
  before: T;
  /** State after the change */
  after: T;
  /** Optional description of the action */
  description?: string;
  /** Timestamp of the action */
  timestamp: number;
}

/** Options for the useHistory hook */
export interface UseHistoryOptions {
  /** Maximum number of history entries to keep */
  maxSize?: number;
  /** Callback when history changes */
  onChange?: () => void;
}

/** Return type for the useHistory hook */
export interface UseHistoryReturn<T> {
  /** Push a new state change to history */
  push: (before: T, after: T, description?: string) => void;
  /** Undo the last action, returns the previous state or null */
  undo: () => T | null;
  /** Redo the last undone action, returns the next state or null */
  redo: () => T | null;
  /** Whether undo is available */
  canUndo: boolean;
  /** Whether redo is available */
  canRedo: boolean;
  /** Clear all history */
  clear: () => void;
  /** Current history index */
  index: number;
  /** Total number of history entries */
  length: number;
  /** Get the current history entry */
  current: HistoryEntry<T> | null;
}

const cloneState = <T>(value: T): T => {
  if (value === null || typeof value !== 'object') return value;
  // Prefer native deep clone to avoid freezing the source object.
  try {
    if (typeof structuredClone === 'function') {
      return structuredClone(value);
    }
  } catch {
    // fall through
  }
  try {
    return JSON.parse(JSON.stringify(value)) as T;
  } catch {
    // Fallback to a shallow clone when deep clone is not possible.
    if (Array.isArray(value)) {
      return value.slice() as T;
    }
    return { ...(value as Record<string, unknown>) } as T;
  }
};

// ============================================================
// HISTORY MANAGER CLASS
// ============================================================

/**
 * HistoryManager class for managing undo/redo state
 * Can be used outside of React components
 */
export class HistoryManager<T = NetworkState> {
  private history: HistoryEntry<T>[] = [];
  private currentIndex: number = -1;
  private maxSize: number;
  private listeners: Set<() => void> = new Set();

  constructor(maxSize: number = HISTORY_CONFIG.maxSize) {
    this.maxSize = maxSize;
  }

  /**
   * Push a new state change to history
   */
  push(before: T, after: T, description?: string): void {
    // Remove any future history (after current index)
    this.history = this.history.slice(0, this.currentIndex + 1);

    // Deep clone the states to prevent mutations
    const entry: HistoryEntry<T> = {
      before: cloneState(before),
      after: cloneState(after),
      description,
      timestamp: Date.now(),
    };

    this.history.push(entry);
    this.currentIndex++;

    // Trim history if it exceeds max size
    if (this.history.length > this.maxSize) {
      this.history.shift();
      this.currentIndex--;
    }

    // Logging
    try {
      log.userAction('History Push', { description });
      log.info(LOG_CATEGORIES.HISTORY, 'History push', { description });
    } catch (e) {}

    this.notify();
  }

  /**
   * Undo the last action
   */
  undo(): T | null {
    if (!this.canUndo()) return null;
    const entry = this.history[this.currentIndex];
    this.currentIndex--;
    this.notify();

    try { log.userAction('History Undo', { description: entry.description }); } catch (e) {}
    try { log.info(LOG_CATEGORIES.HISTORY, 'History undo', { description: entry.description }); } catch (e) {}

    return cloneState(entry.before);
  }

  /**
   * Redo the last undone action
   */
  redo(): T | null {
    if (!this.canRedo()) return null;
    this.currentIndex++;
    const entry = this.history[this.currentIndex];
    this.notify();

    try { log.userAction('History Redo', { description: entry.description }); } catch (e) {}
    try { log.info(LOG_CATEGORIES.HISTORY, 'History redo', { description: entry.description }); } catch (e) {}

    return cloneState(entry.after);
  }

  /**
   * Check if undo is available
   */
  canUndo(): boolean {
    return this.currentIndex >= 0;
  }

  /**
   * Check if redo is available
   */
  canRedo(): boolean {
    return this.currentIndex < this.history.length - 1;
  }

  /**
   * Clear all history
   */
  clear(): void {
    this.history = [];
    this.currentIndex = -1;
    this.notify();
  }

  /**
   * Get current index
   */
  getIndex(): number {
    return this.currentIndex;
  }

  /**
   * Get history length
   */
  getLength(): number {
    return this.history.length;
  }

  /**
   * Get current history entry
   */
  getCurrent(): HistoryEntry<T> | null {
    if (this.currentIndex < 0 || this.currentIndex >= this.history.length) {
      return null;
    }
    return this.history[this.currentIndex];
  }

  /**
   * Subscribe to history changes
   */
  subscribe(callback: () => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of changes
   */
  private notify(): void {
    this.listeners.forEach((callback) => callback());
  }
}

// ============================================================
// SINGLETON INSTANCE (for legacy compatibility)
// ============================================================

/** Global history manager instance for legacy compatibility */
export const historyManager = new HistoryManager<NetworkState>();

// ============================================================
// REACT HOOK
// ============================================================

/**
 * React hook for undo/redo functionality
 * 
 * @param options - Configuration options
 * @returns History control methods and state
 * 
 * @example
 * ```tsx
 * const history = useHistory<NetworkState>();
 * 
 * // Record a change
 * const handleAddNode = (node: Node) => {
 *   const before = { nodes, connections };
 *   const newNodes = [...nodes, node];
 *   setNodes(newNodes);
 *   history.push(before, { nodes: newNodes, connections });
 * };
 * 
 * // Undo
 * const handleUndo = () => {
 *   const state = history.undo();
 *   if (state) {
 *     setNodes(state.nodes);
 *     setConnections(state.connections);
 *   }
 * };
 * ```
 */
export function useHistory<T = NetworkState>(
  options: UseHistoryOptions = {}
): UseHistoryReturn<T> {
  const { maxSize = HISTORY_CONFIG.maxSize, onChange } = options;

  // Create a stable manager instance
  const managerRef = useRef<HistoryManager<T>>();
  if (!managerRef.current) {
    managerRef.current = new HistoryManager<T>(maxSize);
  }
  const manager = managerRef.current;

  // State to trigger re-renders on history changes
  const [, forceUpdate] = useState(0);

  // Subscribe to history changes
  useEffect(() => {
    const unsubscribe = manager.subscribe(() => {
      forceUpdate((n) => n + 1);
      onChange?.();
    });
    return unsubscribe;
  }, [manager, onChange]);

  const push = useCallback(
    (before: T, after: T, description?: string) => {
      manager.push(before, after, description);
    },
    [manager]
  );

  const undo = useCallback(() => {
    return manager.undo();
  }, [manager]);

  const redo = useCallback(() => {
    return manager.redo();
  }, [manager]);

  const clear = useCallback(() => {
    manager.clear();
  }, [manager]);

  return {
    push,
    undo,
    redo,
    canUndo: manager.canUndo(),
    canRedo: manager.canRedo(),
    clear,
    index: manager.getIndex(),
    length: manager.getLength(),
    current: manager.getCurrent(),
  };
}

// ============================================================
// CONVENIENCE HOOK FOR NETWORK STATE
// ============================================================

/**
 * Convenience hook specifically for network state history
 * Integrates with nodes and connections state
 */
export function useNetworkHistory(
  nodes: NetworkState['nodes'],
  connections: NetworkState['connections'],
  setNodes: (nodes: NetworkState['nodes']) => void,
  setConnections: (connections: NetworkState['connections']) => void,
  options: UseHistoryOptions = {}
) {
  const history = useHistory<NetworkState>(options);

  /**
   * Record a state change
   */
  const record = useCallback(
    (
      before: NetworkState,
      after: NetworkState,
      description?: string
    ) => {
      history.push(before, after, description);
    },
    [history]
  );

  /**
   * Perform undo and apply state
   */
  const performUndo = useCallback(() => {
    const state = history.undo();
    if (state) {
      setNodes(state.nodes);
      setConnections(state.connections);
      return true;
    }
    return false;
  }, [history, setNodes, setConnections]);

  /**
   * Perform redo and apply state
   */
  const performRedo = useCallback(() => {
    const state = history.redo();
    if (state) {
      setNodes(state.nodes);
      setConnections(state.connections);
      return true;
    }
    return false;
  }, [history, setNodes, setConnections]);

  /**
   * Create a snapshot of current state for "before"
   */
  const snapshot = useCallback((): NetworkState => {
    return cloneState({ nodes, connections });
  }, [nodes, connections]);

  return {
    ...history,
    record,
    performUndo,
    performRedo,
    snapshot,
  };
}

export default useHistory;
